import { useRef, useState, useEffect } from "react";
import { FaSquare, FaImage, FaMousePointer, FaLink, FaCamera } from "react-icons/fa";
import { toast } from "react-toastify";

const Toolbar = ({ elements, setElements, selectedId, previewMode }) => {
  const fileInputRef = useRef(null);
  const bgImageRef = useRef(null);

  const toolbarRef = useRef(null); // ✅ detect toolbar clicks
  const popupRef = useRef(null);   // ✅ detect popup clicks

  const [showLinkPopup, setShowLinkPopup] = useState(false);
  const [linkValue, setLinkValue] = useState("");

  const [showImagePopup, setShowImagePopup] = useState(false);
  const [imageURL, setImageURL] = useState("");

  const [showBgPopup, setShowBgPopup] = useState(false);
  const [bgURL, setBgURL] = useState("");

  /* ---------------- CLOSE ALL POPUPS ---------------- */
  const closeAllPopups = () => {
    setShowImagePopup(false);
    setShowLinkPopup(false);
    setShowBgPopup(false);
  };

  /* ---------------- OUTSIDE CLICK + CANVAS CLICK ---------------- */
useEffect(() => {
  const handleClickOutside = (event) => {
    const clickedInsidePopup =
      popupRef.current && popupRef.current.contains(event.target);

    // ✅ If click is NOT inside popup → close
    if (!clickedInsidePopup) {
      closeAllPopups();
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);

  /* ---------------- ADD BOX ---------------- */
  const addBox = () => {
    const boxes = elements.filter(el => el.type === "box");
    let newX = 20;

    if (boxes.length > 0) {
      const lastBox = boxes[boxes.length - 1];
      newX = lastBox.x + lastBox.width + 20;
    }

    const blockPreview = () => {
      if (previewMode) return true;
      return false;
    };

    const newBox = {
      id: Date.now(),
      type: "box",
      x: newX,
      y: 60,
      width: 300,
      height: 200,
      background: "#ffffff",
      backgroundImage: null,
      backgroundPosition: "center",
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      borderRadius: 10,
      padding: 10,
      zIndex: 1
    };

    setElements(prev => [...prev, newBox]);
  };

  /* ---------------- ADD TEXT ---------------- */
  const addHeading = () => {
    const box = elements.find(el => el.id === selectedId && el.type === "box");
    if (!box) {
      toast.warning("Please select a box first");
      return;
    }

    const text = {
      id: Date.now(),
      type: "heading",
      parentBox: box.id,
      text: "Edit Text",
      x: box.x + 20,
      y: box.y + 20,
      width: 200,
      height: 60,
      fontSize: "28px",
      color: "#000",
      fontFamily: "Inter",
      fontWeight: "600",
      textAlign: "left",
      opacity: 1,
      rotation: 0,
      zIndex: 3
    };

    setElements(prev => [...prev, text]);
  };

  /* ---------------- ADD BUTTON ---------------- */
  const addButton = () => {
    const box = elements.find(el => el.id === selectedId && el.type === "box");
    if (!box) {
      toast.warning("Select a box first");
      return;
    }

    const button = {
      id: Date.now(),
      type: "button",
      parentBox: box.id,
      text: "Click Me",
      link: "",
      x: box.x + 40,
      y: box.y + 40,
      width: 160,
      height: 50,
      background: "#000",
      color: "#fff",
      borderRadius: 8,
      fontSize: "16px",
      fontWeight: "500",
      fontFamily: "Inter",
      textAlign: "center",
      zIndex: 2
    };

    setElements(prev => [...prev, button]);
  };

  /* ---------------- IMAGE UPLOAD ---------------- */
  const openImagePicker = () => {
    fileInputRef.current.click();
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const url = URL.createObjectURL(file);

    const box = elements.find(el => el.id === selectedId && el.type === "box");
    if (!box) {
      toast.warning("Select a box first");
      return;
    }

    const img = {
      id: Date.now(),
      type: "image",
      parentBox: box.id,
      src: url,
      x: box.x + 20,
      y: box.y + 20,
      width: Math.min(200, box.width - 40),
      height: Math.min(200, box.height - 40),
      zIndex: 2
    };

    setElements(prev => [...prev, img]);
  };

  /* ---------------- BACKGROUND IMAGE ---------------- */
  const setBackgroundImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    const box = elements.find(el => el.id === selectedId && el.type === "box");

    if (!box) {
      toast.warning("Select a box first");
      return;
    }

    setElements(prev =>
      prev.map(el =>
        el.id === selectedId
          ? {
              ...el,
              backgroundImage: url,
              backgroundPosition: "center center",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat"
            }
          : el
      )
    );

    bgImageRef.current.value = "";
  };

  /* ---------------- APPLY LINK ---------------- */
  const applyLink = () => {
    if (!selectedId) return;

    setElements(prev =>
      prev.map(el =>
        el.id === selectedId ? { ...el, link: linkValue } : el
      )
    );

    setLinkValue("");
    setShowLinkPopup(false);
  };

  return (
    <div className="tool-bar" ref={toolbarRef}>
      <button className="toolls-btn" onClick={addBox}>
        <FaSquare />
      </button>

      <button className="toolls-btn" onClick={addHeading}>
        T
      </button>

      <button
        className="toolls-btn"
        onClick={() => {
          closeAllPopups();
          setShowImagePopup(prev => !prev);
        }}
      >
        <FaImage />
      </button>

      <button className="toolls-btn" onClick={addButton}>
        [+]
      </button>

      <button
        className="toolls-btn"
        onClick={() => {
          closeAllPopups();
          setShowBgPopup(prev => !prev);
        }}
      >
        <FaCamera />
      </button>

      <div className="linkk-tooll">
        <button
          className="toolls-btn"
          onClick={() => {
            closeAllPopups();
            setShowLinkPopup(prev => !prev);
          }}
        >
          <FaLink />
        </button>

        {showLinkPopup && (
          <div className="linkk-popupp" ref={popupRef}>
            <input
              type="text"
              placeholder="Enter URL"
              value={linkValue}
              onChange={(e) => setLinkValue(e.target.value)}
            />
            <button onClick={applyLink}>Apply</button>
          </div>
        )}

        {showImagePopup && (
          <div className="linkk-popupp" ref={popupRef}>
            <input
              type="text"
              placeholder="Paste Image URL"
              value={imageURL}
              onChange={(e) => setImageURL(e.target.value)}
            />
            <button
              onClick={() => {
                const box = elements.find(
                  el => el.id === selectedId && el.type === "box"
                );

                if (!box) {
                  toast.warning("Select a box first");
                  return;
                }

                const img = {
                  id: Date.now(),
                  type: "image",
                  parentBox: box.id,
                  src: imageURL,
                  x: box.x + 20,
                  y: box.y + 20,
                  width: Math.min(200, box.width - 40),
                  height: Math.min(200, box.height - 40),
                  zIndex: 2
                };

                setElements(prev => [...prev, img]);

                setImageURL("");
                setShowImagePopup(false);
              }}
            >
              Add
            </button>

            <button onClick={openImagePicker}>Upload</button>
          </div>
        )}

        {showBgPopup && (
          <div className="linkk-popupp" ref={popupRef}>
            <input
              type="text"
              placeholder="Paste Background Image URL"
              value={bgURL}
              onChange={(e) => setBgURL(e.target.value)}
            />

            <button
              onClick={() => {
                const box = elements.find(
                  el => el.id === selectedId && el.type === "box"
                );

                if (!box) {
                  toast.warning("Select a box first");
                  return;
                }

                setElements(prev =>
                  prev.map(el =>
                    el.id === selectedId
                      ? {
                          ...el,
                          backgroundImage: bgURL,
                          backgroundPosition: "center center",
                          backgroundSize: "cover",
                          backgroundRepeat: "no-repeat"
                        }
                      : el
                  )
                );

                setBgURL("");
                setShowBgPopup(false);
              }}
            >
              Apply
            </button>

            <button onClick={() => bgImageRef.current.click()}>
              Upload
            </button>
          </div>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="image/*"
        onChange={handleUpload}
      />

      <input
        type="file"
        ref={bgImageRef}
        style={{ display: "none" }}
        accept="image/*"
        onChange={setBackgroundImage}
      />
    </div>
  );
};

export default Toolbar;